/**
 * 登录这道门。诊断是静态页面，页面本身不验人：数据接口（sidecar）每个请求都问统一认证服务，
 * 没有本后台的通行证（__Host-console_app）就回 401。这里只把 401 变成「去入口换一张」：
 * 入口那边没登录会先让人登录，登录完原路回来（console/docs/specs/统一认证.md「统一登录」）。
 *
 * 角色只决定看到什么（履约与员工看假名版，管理员以外看设备内容要先选理由，sidecar 的 masking.py 与 access.py
 * 在服务端判），这里判断角色只是为了给进不来的人一句话、少显示用不上的东西，真正的门在 sidecar 与统一认证。
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { CircleAlert, KeyRound } from 'lucide-react';
import { apiEvents } from '../api/client';
import { codeOf, loadErrorText } from '../api/errors';
import { useMeQuery } from '../api/queries';
import type { Me } from '../api/types';
import { useAfter } from '../components/blocks';

// 入口的地址是产品约定（console/portal/auth/console_apps.py）。构建期变量只给本地联调覆盖用，生产不设。
export const PORTAL_ORIGIN: string = import.meta.env.VITE_CONSOLE_PORTAL_ORIGIN || 'https://console.pophie.com';

// 能进诊断的角色，同 sidecar auth_client.ALLOWED_ROLES（汤祺 2026-10-07：内部后台对全员开放）
const ALLOWED_ROLES = ['admin', 'dev', 'support', 'ops', 'operator', 'staff'];

/** 去入口换本后台通行证的地址，换完回到 `path` */
export function ssoUrl(path: string): string {
  const u = new URL('/api/portal/sso/authorize', PORTAL_ORIGIN);
  u.search = new URLSearchParams({ app: 'diagnostics', r: path }).toString();
  return u.toString();
}

// 换完通行证回来还是 401（通行证没设上、签错了后台）时不能再跳：会在入口和这里之间来回弹。
// 30 秒内跳过一次就停下，给人一个按钮和一句原因。
const LAST_SSO = 'diag.sso.at';

/** 整页跳转。单独成一个对象，测试里替换它看跳去了哪（jsdom 不能真的跳） */
export const pageNav = { assign: (url: string) => window.location.assign(url) };

function goSso(): boolean {
  const now = Date.now();
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(LAST_SSO)) || 0;
  } catch {
    // 隐私模式下记不住：照常跳
  }
  if (now - last < 30_000) return false;
  try {
    sessionStorage.setItem(LAST_SSO, String(now));
  } catch {
    // 同上
  }
  pageNav.assign(ssoUrl(window.location.pathname + window.location.search));
  return true;
}

const MeContext = createContext<Me | null>(null);

export function useMe(): Me {
  const me = useContext(MeContext);
  if (!me) throw new Error('useMe 要在 AuthGate 里面用');
  return me;
}

/** 看原样的角色：管理员、研发、客服（汤祺 2026-10-07 第二次拍板，客服与研发一样）。同 sidecar masking._RAW_ROLES */
const RAW_ROLES = ['admin', 'dev', 'support'];

/** 履约、员工看到的是假名版。判定与 sidecar masking.should_mask 一致：角色是并集，带看原样的角色之一就看原样 */
export function useIsMasked(): boolean {
  return !useMe().roles.some((r) => RAW_ROLES.includes(r));
}

function Gate({ children }: { children: ReactNode }) {
  return (
    <main className="login" id="main">
      <div className="login-card">{children}</div>
    </main>
  );
}

export function AuthGate({ children }: { children: ReactNode }) {
  const me = useMeQuery();
  const [looped, setLooped] = useState(false);
  const showWait = useAfter();

  // 任何接口回 401（包括 /api/console/me）：去入口换通行证
  useEffect(
    () =>
      apiEvents.onUnauthenticated(() => {
        if (!goSso()) setLooped(true);
      }),
    [],
  );

  if (me.isPending || (me.isError && codeOf(me.error) === 'unauthenticated' && !looped)) {
    return showWait ? (
      <Gate>
        <p className="hint" aria-busy="true">{me.isPending ? '正在确认登录状态…' : '正在去入口登录…'}</p>
      </Gate>
    ) : null;
  }
  if (me.isError) {
    return (
      <Gate>
        <h1 className="row">
          <CircleAlert className="ico" aria-hidden="true" />
          {looped ? '登录没有成功' : '打不开问题诊断'}
        </h1>
        <p className="mt-8">
          {looped
            ? '从入口回来以后，问题诊断还是认不出你的登录。可能是浏览器拦了 cookie，或者入口刚更新过。再试一次；还不行，把这句话发给研发。'
            : loadErrorText(me.error, '登录状态', { endpoint: '/api/console/me' })}
        </p>
        <div className="row mt-16">
          {looped ? (
            <a className="btn btn-primary" href={ssoUrl(window.location.pathname + window.location.search)}>
              <KeyRound className="ico" aria-hidden="true" />
              重新登录
            </a>
          ) : (
            <button type="button" className="btn btn-primary" onClick={() => void me.refetch()}>
              重试
            </button>
          )}
        </div>
      </Gate>
    );
  }
  if (!me.data.roles.some((r) => ALLOWED_ROLES.includes(r))) {
    return (
      <Gate>
        <h1>你的账号不能进入问题诊断</h1>
        <p className="mt-8">问题诊断给管理员、研发、客服、履约和员工用，你的账号还没有这几个角色。需要的话找管理员在入口加角色。</p>
        <div className="row mt-16">
          <a className="btn" href={`${PORTAL_ORIGIN}/`}>回到入口</a>
        </div>
      </Gate>
    );
  }
  return <MeContext.Provider value={me.data}>{children}</MeContext.Provider>;
}
